import React from 'react';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import type { SubHomeContent } from './types';

/**
 * Valor do campo "Imagem de destaque" do conteúdo (`preview_image_link`, do
 * behavior `volto.preview_image_link`).
 *
 * É uma relação para um objeto Image, serializada como resumo. O `@id` vem
 * sempre; `image_scales` só quando o serializer de resumo o inclui — daí as
 * duas formas de resolver abaixo.
 */
export interface PreviewImageLink {
  '@id'?: string;
  title?: string;
  image_field?: string;
  image_scales?: Record<string, unknown>;
}

/**
 * Props de imagem prontas para o `<Image>` do Volto, ou `null` quando o campo
 * está vazio.
 *
 * Com `image_scales`, devolve o próprio item e o `<Image>` monta o `srcset`
 * responsivo; sem ele, cai na escala cheia do Plone. Nunca congelamos a URL
 * de uma escala: elas carregam o hash do binário, e trocar a foto do
 * conteúdo quebraria o banner.
 */
export const getSubHomeBannerImageProps = (
  preview?: PreviewImageLink | null,
): { item: PreviewImageLink } | { src: string } | null => {
  if (!preview?.['@id']) return null;
  if (preview.image_scales) return { item: preview };
  return { src: flattenToAppURL(`${preview['@id']}/@@images/image`) };
};

/**
 * Banner do topo da sub-home, vindo da "Imagem de destaque" do documento.
 *
 * O design abre a página com uma faixa de banner, e a origem dela é o campo
 * do próprio conteúdo — não um bloco. Quem preenche a imagem de destaque vê
 * a faixa aparecer no topo, sem precisar montar bloco nenhum; quem deixa o
 * campo vazio não ganha faixa alguma e pode abrir a página com o bloco
 * Banner ou Imagem, se quiser um banner rotativo ou com link.
 *
 * A legenda sai de `preview_caption_link`, o par do mesmo behavior. O `alt`
 * usa o título do conteúdo em vez de repetir a legenda: com as duas iguais,
 * quem navega por leitor de tela ouviria o mesmo texto duas vezes.
 */
export const SubHomeBanner: React.FC<{ content: SubHomeContent }> = ({
  content,
}) => {
  const imageProps = getSubHomeBannerImageProps(content?.preview_image_link);
  if (!imageProps) return null;

  const legenda = content?.preview_caption_link;

  return (
    <figure className="mpf-subhome__banner">
      <Image
        {...imageProps}
        alt={content?.preview_alt_link || content?.title || ''}
        // `eager`: é o primeiro elemento visível da página, então adiar a
        // carga só produziria um salto de layout logo na abertura.
        loading="eager"
        responsive={true}
        className="mpf-subhome__banner-img"
      />
      {legenda && (
        <figcaption className="mpf-image-caption">{legenda}</figcaption>
      )}
    </figure>
  );
};

export default SubHomeBanner;
